import { useCallback, useEffect, useState } from 'react';

import { scrollToY } from './lenis_scroll';
import {
  NAV_SECTION_ORDER,
  NAV_SECTION_TARGETS,
  type NavSectionId,
} from './nav_sections';

const getScrollOffset = () => {
  const header = document.querySelector('header');
  return (header?.getBoundingClientRect().height ?? 98) + 8;
};

export const useNavScroll = () => {
  const [activeSection, setActiveSection] = useState<NavSectionId | null>(null);

  const scrollToSection = useCallback((navId: NavSectionId) => {
    const targetId = NAV_SECTION_TARGETS[navId][0];
    const el = document.getElementById(targetId);
    if (!el) {
      return;
    }

    const top =
      el.getBoundingClientRect().top + window.scrollY - getScrollOffset();
    scrollToY(top);
    history.replaceState(null, '', `#${targetId}`);
  }, []);

  useEffect(() => {
    const updateActiveSection = () => {
      const offset = window.scrollY + getScrollOffset();
      let next: NavSectionId | null = null;

      const firstTargetId = NAV_SECTION_TARGETS[NAV_SECTION_ORDER[0]][0];
      const firstSection = document.getElementById(firstTargetId);
      if (firstSection) {
        const firstSectionTop =
          firstSection.getBoundingClientRect().top + window.scrollY;
        if (offset < firstSectionTop) {
          setActiveSection(null);
          return;
        }
      }

      for (const navId of NAV_SECTION_ORDER) {
        const targetId = NAV_SECTION_TARGETS[navId][0];
        const el = document.getElementById(targetId);
        if (!el) {
          continue;
        }

        const sectionTop = el.getBoundingClientRect().top + window.scrollY;
        if (sectionTop <= offset) {
          next = navId;
        }
      }

      setActiveSection(next);
    };

    updateActiveSection();
    window.addEventListener('scroll', updateActiveSection, { passive: true });
    window.addEventListener('resize', updateActiveSection);

    return () => {
      window.removeEventListener('scroll', updateActiveSection);
      window.removeEventListener('resize', updateActiveSection);
    };
  }, []);

  return { activeSection, scrollToSection };
};
